import { ArrowRight } from "lucide-react";

const HIGHLIGHTS = [
    {
        title: "Multiple native growth factors",
        body: "Including BMP-2, BMP-7, PDGF, VEGF, and TGF-β1.",
    },
    {
        title: "NMP processing",
        body: "Makes growth factors within the bone matrix more bioavailable.",
    },
    {
        title: "Bone-based composition",
        body: "Processed bone fibers and microparticulates combined with mineralized cancellous bone.",
    },
];

const BROCHURE_URL =
    "https://drive.google.com/file/d/1nL97CndBRP1nYOxPcBs3scrW_HdQI37A/view";
const WEBSITE_URL = "https://www.inducext.com/";

/**
 * InduceXT launch-product spotlight for the Home page. Mirrors the /inducext
 * hero content inside the "Bone graft / Orthobiologics" opportunity card.
 */
export default function InduceXTSpotlight() {
    return (
        <section
            id="inducext"
            data-testid="inducext-spotlight-section"
            className="bg-[#FAFAFA] border-b border-slate-200 scroll-mt-24"
        >
            <div className="max-w-6xl mx-auto px-6 md:px-12 py-20 md:py-28">
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 mb-12">
                    <div className="lg:col-span-6">
                        <p className="eyebrow">The Launch Product</p>
                        <h2
                            data-testid="inducext-headline"
                            className="font-display mt-4 text-4xl md:text-6xl font-semibold tracking-tighter text-slate-900 leading-[1.05]"
                        >
                            InduceXT.
                            <br />
                            <span className="text-slate-500">
                                Available nationwide.
                            </span>
                        </h2>
                    </div>
                    <div className="lg:col-span-6 lg:pt-3">
                        <p className="text-base md:text-lg text-slate-600 leading-relaxed">
                            InduceXT is 1099er&rsquo;s launch product line — available
                            to experienced orthopedic, biologics, ASC, and hospital OR
                            reps across the United States. Add it to your bag without
                            disrupting your existing lines.
                        </p>
                    </div>
                </div>

                {/* Product card — mirrors the /inducext hero */}
                <article
                    data-testid="inducext-product-card"
                    className="border border-slate-200 bg-white rounded-2xl p-7 md:p-12"
                >
                    <span className="text-[11px] font-medium uppercase tracking-[0.14em] text-slate-500">
                        Bone graft / Orthobiologics
                    </span>

                    <div className="mt-6">
                        <h3 className="sr-only">Induce XT — NMP Bone Graft</h3>
                        <img
                            src="/inducext-wordmark.jpg"
                            alt="Induce XT — NMP Bone Graft"
                            data-testid="inducext-card-wordmark"
                            className="w-full max-w-[520px] h-auto"
                            loading="lazy"
                        />
                    </div>

                    <p
                        data-testid="inducext-card-subheadline"
                        className="mt-7 font-display text-2xl md:text-4xl font-semibold tracking-tight text-slate-500 leading-[1.15] max-w-3xl"
                    >
                        A bone allograft with multiple growth factors.
                    </p>
                    <p className="mt-5 text-base md:text-lg text-slate-600 leading-relaxed max-w-2xl">
                        Induce XT uses proprietary Natural Matrix Protein (NMP)
                        processing to increase the bioavailability of growth factors
                        naturally present in donor bone&mdash;including BMP-2, BMP-7,
                        PDGF, VEGF, and TGF-β1&mdash;for bone regeneration.
                    </p>

                    <ul
                        data-testid="inducext-card-highlights"
                        className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-3"
                    >
                        {HIGHLIGHTS.map((h) => (
                            <li
                                key={h.title}
                                data-testid={`inducext-card-highlight-${h.title.toLowerCase().replace(/\s+/g, "-")}`}
                                className="rounded-xl border border-slate-200 bg-[#FAFAFA] p-4"
                            >
                                <p className="font-display text-sm md:text-base font-semibold text-slate-900 leading-snug">
                                    {h.title}
                                </p>
                                <p className="mt-1.5 text-sm text-slate-600 leading-relaxed">
                                    {h.body}
                                </p>
                            </li>
                        ))}
                    </ul>

                    <div className="mt-10 flex flex-col sm:flex-row flex-wrap gap-3">
                        <a
                            href="#join"
                            data-testid="inducext-become-1099er-btn"
                            className="inline-flex items-center justify-center gap-2 bg-slate-900 text-white text-sm md:text-base font-medium px-7 py-3.5 rounded-md hover:bg-slate-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-900 focus-visible:ring-offset-2 transition-colors"
                        >
                            Schedule a Confidential Call
                            <ArrowRight size={16} />
                        </a>
                        <a
                            href={BROCHURE_URL}
                            target="_blank"
                            rel="noopener noreferrer"
                            data-testid="inducext-brochure-btn"
                            className="inline-flex items-center justify-center gap-2 bg-white text-slate-900 border border-slate-300 text-sm md:text-base font-medium px-7 py-3.5 rounded-md hover:border-slate-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-900 focus-visible:ring-offset-2 transition-colors"
                        >
                            View Product Brochure
                            <ArrowRight size={16} />
                        </a>
                        <a
                            href={WEBSITE_URL}
                            target="_blank"
                            rel="noopener noreferrer"
                            data-testid="inducext-website-btn"
                            className="inline-flex items-center justify-center gap-2 bg-white text-slate-900 border border-slate-300 text-sm md:text-base font-medium px-7 py-3.5 rounded-md hover:border-slate-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-900 focus-visible:ring-offset-2 transition-colors"
                        >
                            Visit Induce XT Website
                            <ArrowRight size={16} />
                        </a>
                    </div>
                </article>

            </div>
        </section>
    );
}
